import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';

// Components
import MyButton from '../../utils/MyButton';
import LikedButton from './LikedButton';
import Comments from './Comments';
import CommentForm from './CommentForm';

// Redux
import { connect } from 'react-redux';
import { getScream, clearErrors } from '../../redux/actions/dataActions';

// MUI stuff
import { withStyles } from '@mui/styles';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';

// Icons
import UnfoldMore from '@mui/icons-material/UnfoldMore';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from '@mui/icons-material/Chat';

const styles = {
    profileImage: {
        width: 150,
        height: 150,
        objectFit: 'cover',
        borderRadius: '50%',
        display: 'block',
        margin: '0 auto'
    },
    dialogContent: {
        padding: '16px 24px 24px'
    },
    dialogTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 8px 8px 24px'
    },
    spinnerDiv: {
        textAlign: 'center',
        marginTop: 50,
        marginBottom: 50
    },
    likeCount: {
        verticalAlign: 'middle',
        marginRight: 8
    },
    commentCount: {
        verticalAlign: 'middle'
    },
    actionsRow: {
        display: 'flex',
        alignItems: 'center',
        marginTop: 8
    }
};

class ScreamDialog extends Component {

    state = {
        open: false,
        oldPath: null,
        newPath: null
    };

    componentDidMount() {
        if (this.props.openDialog) {
            this.handleOpen();
        }
    }

    handleOpen = () => {
        let oldPath = window.location.pathname;
        const { userHandle, screamId } = this.props;
        const newPath = `/users/${userHandle}/scream/${screamId}`;
        if (oldPath === newPath) oldPath = `/users/${userHandle}`;
        window.history.pushState(null, null, newPath);
        this.setState({ open: true, oldPath, newPath });
        this.props.getScream(this.props.screamId);
    }

    handleClose = () => {
        window.history.pushState(null, null, this.state.oldPath);
        this.setState({ open: false });
        this.props.clearErrors();
    }

    render() {
        const {
            classes,
            scream: { scream },
            UI: { loading }
        } = this.props;

        const dialogMarkup = loading
            ? (
                <div className={classes.spinnerDiv}>
                    <CircularProgress size={80} thickness={2} />
                </div>
            )
            : (
                scream && (
                    <Grid container spacing={3} alignItems="flex-start">
                        <Grid item xs={12} sm={4} style={{ textAlign: 'center' }}>
                            <img src={scream.userImage} alt="Profile" className={classes.profileImage} />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <Typography
                                variant="h5"
                                component={Link}
                                to={`/users/${scream.userHandle}`}
                                color="primary"
                                style={{ textDecoration: 'none' }}>
                                @{scream.userHandle}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" style={{ marginTop: 4 }}>
                                {dayjs(scream.createdAt).format('h:mm a, MMMM DD YYYY')}
                            </Typography>
                            <Typography variant="body1" style={{ marginTop: 8, marginBottom: 8 }}>
                                {scream.body}
                            </Typography>
                            <Box className={classes.actionsRow}>
                                <LikedButton screamId={scream.id || scream.screamId} />
                                <Typography variant="body2" component="span" className={classes.likeCount}>
                                    {scream.likeCount} Likes
                                </Typography>
                                <MyButton tip="Comments">
                                    <ChatIcon color="primary" />
                                </MyButton>
                                <Typography variant="body2" component="span" className={classes.commentCount}>
                                    {scream.commentCount} Comments
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <Divider />
                        </Grid>
                        <CommentForm screamId={scream.screamId || scream.id} />
                        <Grid item xs={12}>
                            <Divider style={{ marginBottom: 8 }} />
                        </Grid>
                        <Comments comments={scream.comments} />
                    </Grid>
                )
            );

        return (
            <>
                <MyButton tip="Expand Scream" onClick={this.handleOpen}>
                    <UnfoldMore color="primary" />
                </MyButton>
                <Dialog
                    open={this.state.open}
                    onClose={this.handleClose}
                    fullWidth
                    maxWidth="md">
                    <DialogTitle disableTypography className={classes.dialogTitle}>
                        <Typography variant="h6">Scream</Typography>
                        <IconButton onClick={this.handleClose} size="small" aria-label="close">
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <Divider />
                    <DialogContent className={classes.dialogContent}>
                        {dialogMarkup}
                    </DialogContent>
                </Dialog>
            </>
        )
    }
}

ScreamDialog.propTypes = {
    getScream: PropTypes.func.isRequired,
    clearErrors: PropTypes.func.isRequired,
    screamId: PropTypes.string.isRequired,
    userHandle: PropTypes.string.isRequired,
    scream: PropTypes.object.isRequired,
    UI: PropTypes.object.isRequired,
    classes: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
    scream: state.data,
    UI: state.UI
});

export default connect(mapStateToProps, { getScream, clearErrors })(withStyles(styles)(ScreamDialog));
